import SystemConst from './constants';
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { IProvincesResponse, ProductGroupByProvince, RegionItem } from '~/types/types';
import { IProduct } from '~/types/address';
dayjs.extend(utc)
dayjs.extend(customParseFormat)
export function formatNumber(n: number, digits = 1) {
  const rounded = Number(n.toFixed(digits));
  return Number.isFinite(rounded) ? rounded.toLocaleString('vi-VN') : "";
}

// Format total price to either `Tỷ` or `trăm triệu` depending on magnitude.
export function formatVNPriceTyOrTramTrieu(value?: number | null) {
  if (!value || value <= 0) return "";
  if (value >= 1e9) {
    return `${formatNumber(value / 1e9, 1)} Tỷ`;
  } else if (value >= 1e6){
    return `${formatNumber(value / 1_000_000_000, 2)} Tỷ`;
  }
  return `${formatNumber(value / 1e6, 2)} triệu`;
}

// Convert to million (triệu)
export function toTrieu(value?: number | null) {
  if (!value || value <= 0) return 0;
  return Number(formatNumber(value / 1_000_000));
}

export function formatPricePerM2(value?: number | null) {
  if (!value || value <= 0) return "";

  if (value >= 1e9) {
    return `${Number((value / 1e9).toFixed(2))} tỷ/m²`;
  }
  if (value >= 1e6) {
    return `${Number((value / 1e6).toFixed(1))} triệu/m²`;
  }

  return value.toLocaleString('vi-VN') + " đ/m²";

}

export const formatTimeUtc7 = (date?: string) => {
  if (!date) return ''

  return dayjs(date, 'YYYY-MM-DD').isValid()
    ? dayjs.utc(date, 'YYYY-MM-DD HH:mm').utcOffset(7).format(SystemConst.FORMATS_DATE.DD_MM_YYYY)
    : ''
}

export const hostName = (path?: string) => {
  if (!path){
    return ''
  }
  return  path.includes(import.meta.env.VITE_API_CMS_URL || '') ? path : import.meta.env.VITE_API_CMS_URL + path || ''
}



const toMs = (d?: string) => (d ? Date.parse(d) || 0 : 0);

export function flattenFromGroups(groups: ProductGroupByProvince[] = []): IProduct[] {
  return groups.flatMap(g => g?.products ?? []);
}
export function top3NewestFromGroups(groups: ProductGroupByProvince[] = []): IProduct[] {
  // flatten
  const all = flattenFromGroups(groups);

  // dedupe theo id, ưu tiên bản có latestUpdatedDate mới hơn
  const map = new Map<string, IProduct>();
  for (const p of all) {
    const cur = map.get(p.id);
    if (!cur || toMs(p.latestUpdatedDate) > toMs(cur.latestUpdatedDate)) {
      map.set(p.id, p);
    }
  }

  // sort createdDate (desc), tie-break latestUpdatedDate
  return [...map.values()]
    .sort((a, b) => {
      const c = toMs(b.createdDate) - toMs(a.createdDate);
      return c !== 0 ? c : toMs(b.latestUpdatedDate) - toMs(a.latestUpdatedDate);
    })
    .slice(0, 3);
}




export function buildRegionItems(
  res: IProvincesResponse,
  opts: { allLabel?: string; sortDesc?: boolean } = {}
): RegionItem[] {
  const allLabel = opts.allLabel ?? "Tất cả các khu vực";

  const items: RegionItem[] = (res.provinces ?? []).map((p) => ({
    id: p.id,
    label: p.name,
    code: p.code,
    count: p.totalJobCount ?? 0,
  }));

  // dùng total từ API nếu có, nếu không thì tự cộng
  const total =
    typeof res.totalJobCount === "number"
      ? res.totalJobCount
      : items.reduce((s, i) => s + (i.count || 0), 0);

  if (opts.sortDesc) {
    items.sort((a, b) => b.count - a.count);
  }

  const allItem: RegionItem = {
    id: "all",
    label: allLabel,
    count: total,
    isAll: true,
  };

  return [allItem, ...items];
}
export function formatDeadline(value: string) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
    .format(date)
    .replace(/\//g, '.');
}


// ---------- Length helpers ----------
export function toMeters(n: number, unit?: string): number {
  const u = (unit || "m").toLowerCase();
  if (["m", "meter", "metre", "mét"].includes(u)) return n;
  if (u === "cm") return n / 100;
  if (u === "mm") return n / 1000;
  if (u === "ft" || u === "feet") return n * 0.3048;
  return n; // mặc định coi là mét
}

export function parseLengthToM(input: unknown): number | null {
  console.log(input)
  if (typeof input === "number" && Number.isFinite(input)) return input;
  if (typeof input !== "string") return null;

  const s = input.trim().toLowerCase().replace(/,/g, "."); // 12,5 -> 12.5
  const m = s.match(/^([+-]?\d+(?:\.\d+)?)\s*(m|meter|metre|mét|cm|mm|ft|feet)?$/i);
  if (!m) return null;
  const value = parseFloat(m[1]);
  const unit = m[2];
  if (!Number.isFinite(value)) return null;
  return toMeters(value, unit);
}

// ---------- Area parser ----------
type AreaParseOptions = {
  /** Chọn giá trị khi là khoảng: "min" | "max" | "avg" (mặc định: "max") */
  treatRange?: "min" | "max" | "avg";
  /** Làm tròn thập phân (mặc định: 2). Set undefined để giữ nguyên. */
  decimals?: number;
  /** Đơn vị mặc định nếu chỉ là số (mặc định "m2") */
  defaultUnit?: "m2" | "ft2" | "cm2";
};

function roundMaybe(n: number, decimals?: number) {
  if (typeof decimals !== "number") return n;
  const p = Math.pow(10, decimals);
  return Math.round(n * p) / p;
}

export function parseAreaToM2(input: unknown, opts: AreaParseOptions = {}): number | null {
  const { treatRange = "max", decimals = 2, defaultUnit = "m2" } = opts;

  // number => coi là m2 luôn
  if (typeof input === "number" && Number.isFinite(input)) return roundMaybe(input, decimals);
  if (typeof input !== "string") return null;

  let s = input.trim().toLowerCase();
  s = s.replace(/\s+/g, " ");
  s = s.replace(/,/g, "."); // 12,5 -> 12.5
  s = s.replace(/㎡/g, "m2").replace(/m\^2/g, "m2").replace(/sq\s*m/g, "sqm");

  // 1) Dạng hình chữ nhật: "3m x 4m", "300cm x 400cm", "3 x 4 m", "3x4"
  {
    const rec = s.match(
      /([+-]?\d+(?:\.\d+)?)\s*(m|meter|metre|mét|cm|mm|ft|feet)?\s*[x×*]\s*([+-]?\d+(?:\.\d+)?)\s*(m|meter|metre|mét|cm|mm|ft|feet)?/
    );
    if (rec) {
      const n1 = parseFloat(rec[1]);
      const u1 = rec[2];
      const n2 = parseFloat(rec[3]);
      const u2 = rec[4] || u1 || "m"; // nếu chỉ ghi đơn vị 1 bên -> áp cho cả 2
      if (Number.isFinite(n1) && Number.isFinite(n2)) {
        const a = toMeters(n1, u1);
        const b = toMeters(n2, u2);
        return roundMaybe(a * b, decimals);
      }
    }
  }

  // 2) Dạng có đơn vị diện tích: "20 m2" | "20 sqm" | "150 sqft" | "150 ft2" | "30000 cm2"
  {
    const area = s.match(/^([+-]?\d+(?:\.\d+)?)\s*(m2|sqm|sq\s*m|ft2|sqft|cm2)$/i);
    if (area) {
      const v = parseFloat(area[1]);
      if (!Number.isFinite(v)) return null;
      const u = area[2].replace(/\s+/g, "");
      let m2 = v;
      if (u === "m2" || u === "sqm" || u === "sq m") m2 = v;
      else if (u === "ft2" || u === "sqft") m2 = v * 0.09290304;
      else if (u === "cm2") m2 = v / 10_000;
      return roundMaybe(m2, decimals);
    }
  }

  // 3) Khoảng: "14-16 m2", "14 ~ 16", "14 to 16"
  {
    const range = s.match(
      /([+-]?\d+(?:\.\d+)?)\s*(?:-|–|~|to|đến)\s*([+-]?\d+(?:\.\d+)?)(?:\s*(m2|sqm|ft2|sqft|cm2))?/
    );
    if (range) {
      const a = parseFloat(range[1]);
      const b = parseFloat(range[2]);
      const unit = range[3] || defaultUnit;
      if (Number.isFinite(a) && Number.isFinite(b)) {
        const pick = treatRange === "min" ? Math.min(a, b) : treatRange === "avg" ? (a + b) / 2 : Math.max(a, b);
        return parseAreaToM2(`${pick} ${unit}`, { decimals, defaultUnit });
      }
    }
  }

  // 4) Chỉ là số -> dùng defaultUnit
  {
    const num = s.match(/^([+-]?\d+(?:\.\d+)?)$/);
    if (num) {
      const v = parseFloat(num[1]);
      if (!Number.isFinite(v)) return null;
      return parseAreaToM2(`${v} ${defaultUnit}`, { decimals, defaultUnit });
    }
  }

  return null; // không nhận dạng được
}


// parseAreaToM2("20");            // 20 (m²)
// parseAreaToM2("20 m2");         // 20
// parseAreaToM2("3m x 4m");       // 12
// parseAreaToM2("300cm x 400cm"); // 12
// parseAreaToM2("150 sqft");      // 13.94
// parseAreaToM2("14-16 m2");      // 16 (mặc định lấy max)
// parseAreaToM2("14~16", { treatRange: "avg" }); // 15

// parseLengthToM("3.5m");   // 3.5
// parseLengthToM("350cm");  // 3.5
// parseLengthToM("10 ft");  // ~3.048
